import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import {
  borderRadius,
  Card,
  Flex,
  space,
  SystemIcon,
  TertiaryButton,
  Text,
} from '@workday/canvas-kit-react'
import { locationIcon } from '@workday/canvas-system-icons-web'
import { GoogleMap, Polygon, useJsApiLoader } from '@react-google-maps/api'
import { AgencyModel, CenteredLayout, EmptyState, imageURLWithSize, PPLoadingDots, fetchAgencies, SocialMediaEndPoint, fetchAgencyShape, fetchAgencyUnitLegend, UnitLegendModel } from 'pulsepoint-web-common'
import { useLocation, useParams } from 'react-router-dom'
import AppSkeleton from 'app/common/AppSkeleton'

const StyledSystemIcon = styled(SystemIcon)`
  margin-right: 6px;
  vertical-align: middle;
`
const StyledTertiaryButton = styled(TertiaryButton)`
  padding: 1px 0px 0px 0px !important;
  width: 50px !important;
  height: 50px !important;

  img {
    width: 50px;
    background: rgb(28, 45, 61);
    border-radius: 10px;
  }
`

const AgencyProfile = () => {
  const { agencyId } = useParams()
  const location = useLocation()
  const [map, setMap] = useState<any | null>(null)
  const [agency, setAgency] = useState<AgencyModel>()
  const [units, setUnits] = useState<UnitLegendModel[]>()
  const [shape, setShape] = useState<string>()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (location.state && location.state.agency && location.state.agency.hasAgencyImage) {
      setAgency(location.state.agency)
    } else if (agencyId) {
      setLoading(true)
      fetchAgencies(
        [agencyId],
        '',
        false,
        'PRODUCTION'
      ).then(loadedAgencies => {
        console.log(loadedAgencies[0])
          setAgency(loadedAgencies[0])
      }).finally(() => setLoading(false))
    }
  }, [])

  useEffect(() => {
    if (map == null || agency == null) {
      return
    }
    const bounds = new window.google.maps.LatLngBounds()
    agency.boundaryCornerCoordinates().forEach(coordinate => bounds.extend(coordinate))
    map.fitBounds(bounds)

  }, [map, agency])

  useEffect(() => {
    if (agency == null) return

    fetchAgencyShape(
      agency.ogr_fid,
      '',
      'PRODUCTION'
    ).then(shape => {
        setShape(shape)
    })

    fetchAgencyUnitLegend(
      agency.agencyID,
      '',
      'PRODUCTION'
    ).then(units => {
      setUnits(units)
    })
  }, [agency])

  const { isLoaded: isMapLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY as string
  })

  return (
    <AppSkeleton>
    { loading ? (
      <PPLoadingDots />
    ) : agency ? (
      <CenteredLayout>
        <Flex flexDirection="column" gap={space.xs}>
          {isMapLoaded && (
            <Card padding={0}>
              <GoogleMap
                onLoad={map => {
                  setMap(map)
                }}
                mapContainerStyle={{
                  height: '400px',
                  width: '100%',
                  borderRadius: borderRadius.l
                }}
                options={{
                  rotateControl: false,
                  panControl: false,
                  streetViewControl: false,
                  scaleControl: false
                }}
              >
                { shape && (
                  <Polygon
                    paths={parsePolyStrings(shape)}
                    options={{
                      strokeColor: '#FF0000',
                      strokeOpacity: 0.8,
                      strokeWeight: 2,
                      fillColor: '#FF0000',
                      fillOpacity: 0.35
                    }}
                  />
                )}
              </GoogleMap>
            </Card>
          )}
          <Card>
            <Flex flexDirection="row" gap={space.xs} alignItems="center">
              {agency.hasAgencyImage() && (
                <img
                  src={imageURLWithSize(agency.agencyImageURI, 60, 60, 1)}
                  srcSet={`${imageURLWithSize(agency.agencyImageURI, 60, 60, 2)} 2x`}
                />
              )}
              <Text typeLevel="body.large" fontWeight="bold">
                {agency.name}
              </Text>
            </Flex>
            <Flex>
              <Text typeLevel="body.medium">
                {agency.detailedDescription.split('\n').map((line, index) => (
                  <p key={index}>{line}</p>
                ))}
              </Text>
            </Flex>
          </Card>
          <Card>
            <Card.Heading>
              {agency.cities.length > 0 && (
                <StyledSystemIcon icon={locationIcon} />
              )}
              Serving
            </Card.Heading>
            <Text typeLevel="body.medium">
              {agency.cities.map((city, index) => {
                return index === agency.cities.length - 1 ? `${city.city}` : `${city.city}, `
              })}
            </Text>
          </Card>
          {units && 
            <Card>
              <Card.Heading>
                Unit Legend
              </Card.Heading>
              <Text typeLevel="body.small">  
                <table><tbody>
                  {units.map((unit, index) => {
                    return <tr key={index}><td style={{paddingRight: '20px'}}>{unit.key}</td><td>{unit.desc}</td></tr>
                  })}
                </tbody></table>
              </Text>
            </Card>
          }
          <br />
          <Flex flexDirection="row" gap={space.xs} alignItems="center" justifyContent='center'>
            {agency.socialMediaEndPoints.map((social, index) => (
                <StyledTertiaryButton key={index} onClick={() => window.location.href = getSocialURL(social)}>
                  <img src={`/images/social_${social.type}_icon.png`} />
                </StyledTertiaryButton>
              )
            )}
          </Flex>
          <br />
          <Flex flexDirection="row" gap={space.xs} alignItems="center" justifyContent='center'>
            {agency.sponsorImageURI && (
              <img
              src={imageURLWithSize(agency.sponsorImageURI, 400, 400, 1)}
              srcSet={`${imageURLWithSize(agency.sponsorImageURI, 400, 400, 2)} 2x`}
            />
            )}
          </Flex>
        </Flex>
      </CenteredLayout>
    ) : (
      <EmptyState>
        <EmptyState.Heading>Failed to load agency profile</EmptyState.Heading>
      </EmptyState>
    )}
    </AppSkeleton>
  )
}

const getSocialURL = (social: SocialMediaEndPoint): string => {
  switch(social.type) {
      case 'facebook':
        return `https://www.facebook.com/${social.value}`
      case 'twitter':
        return `https://www.x.com/${social.value}`
      case 'instagram':
        return `https://www.instagram.com/${social.value}`
      case 'youTube':
        return `https://www.youtube.com/${social.value}`
      case 'linkedIn':
        return `https://www.linkedin.com/company/${social.value}`
      case 'flickr':
        return ''
      case 'email':
        return `mailto:${social.value}`
      case 'website':
        return social.value
  }
}

const parsePolyStrings = (ps: string) => {
  var i,
    j,
    lat,
    lng,
    tmp,
    tmpArr,
    arr = [],
    //match '(' and ')' plus contents between them which contain anything other than '(' or ')'
    m = ps.match(/\([^\(\)]+\)/g)
  if (m !== null) {
    for (i = 0; i < m.length; i++) {
      //match all numeric strings
      tmp = m[i].match(/-?\d+\.?\d*/g)
      if (tmp !== null) {
        //convert all the coordinate sets in tmp from strings to Numbers and convert to LatLng objects
        for (j = 0, tmpArr = []; j < tmp.length; j += 2) {
          lng = Number(tmp[j])
          lat = Number(tmp[j + 1])
          tmpArr.push(new window.google.maps.LatLng(lat, lng))
        }
        arr.push(tmpArr)
      }
    }
  }
  return arr
}

export default AgencyProfile
